/**
 * SceneManager - Manages scene zones, waypoints, and object parsing from GLB files.
 */

import * as THREE from 'three'
import { AnimationManager } from './AnimationManager'
import { AudioManager } from './AudioManager'
import { debugLog, debugWarn, debugError } from '../utils/debug.js'

const CAMERA_FRAMING_PADDING = 1.15
const DEFAULT_INTERACTABLE_ZONE = '_default_interactable_zone'


export class SceneManager
{
  /** @type {THREE.Object3D} */
  scene = null

  /** @type {Object} */
  controls = null

  /** @type {SceneZone[]} */
  sceneZones = []

  /** @type {Waypoint[]} */
  waypoints = []

  /** @type {SiteData} */
  siteData = {}

  /** @type {THREE.Object3D[]} */
  physicsObjects = []
  interactables = []

  /** @type {MediaObjects} */
  mediaObjects = {
    video: [],
    audio: [],
    positionalAudio: [],
  }

  /** @type {AnimationManager} */
  animationManager = null

  /** @type {AudioManager} */
  audioManager = null

  /**
   * Create a new SceneManager.
   * @param {THREE.Object3D} scene - The loaded GLB scene
   * @param {Object} controls - Camera controls instance
   * @param {THREE.AnimationClip[]} animations - Animation clips
   * @param {Object} actions - Animation actions map
   * @param {THREE.AnimationMixer} mixer - Animation mixer
   */
  constructor(scene, controls, animations, actions, mixer)
  {
    this.scene = scene
    this.controls = controls

    // Initialize sub-managers
    this.animationManager = new AnimationManager(animations, actions)
    this.audioManager = new AudioManager(controls.camera)

    // Parse scene structure
    this.populateSceneZones(scene)
    this.fixWaypoints()
    this.fixZones()

    // Start looping animations
    this.animationManager.playLoopingAnimations()

    // Set up sound initialization on first interaction
    this.setupSoundInitialization()

    // Read verbose flag
    this.verbose = new URLSearchParams(window.location.search).get('verbose') === 'true'
  }

  /**
   * Set up first-click sound initialization.
   * @private
   */
  setupSoundInitialization()
  {
    const playLoopingSounds = () =>
    {
      this.audioManager.playLoopingSounds()
      window.removeEventListener('mousedown', playLoopingSounds)
    }
    window.addEventListener('mousedown', playLoopingSounds)
  }

  /**
   * Play an animation by name.
   * @param {string} name - Animation name
   * @param {THREE.AnimationLoopMode} loopType - Loop mode (default: LoopOnce)
   * @returns {Promise|null} Promise that resolves when animation completes
   */
  playAnimation = (name, loopType = THREE.LoopOnce) =>
  {

    debugLog('[SceneManager] playAnimation:', name, loopType)

    return this.animationManager.playAnimation(name, loopType)
  }

  /**
   * Play a sound.
   * @param {string} name - Sound name
   * @param {boolean} loop - Whether to loop the sound
   * @returns {Object|null} Audio object
   */
  playSound = (name, loop = false) =>
  {
    return this.audioManager.playSound(name, loop)
  }

  /**
   * Get an animation action by name.
   * @param {string} name - Animation name
   * @returns {THREE.AnimationAction|undefined}
   */
  getAnimationAction(name)
  {
    return this.animationManager.actions[ name ]
  }

  /**
   * Get all animation actions bound to an object.
   * @param {THREE.Object3D} obj - Target object
   * @returns {THREE.AnimationAction[]}
   */
  getBoundedActions(obj)
  {
    return this.animationManager.getBoundedActions(obj)
  }

  /**
   * Get all scene zones.
   * @returns {SceneZone[]}
   */
  getSceneZones()
  {
    return this.sceneZones
  }

  /**
   * Get a scene zone by name.
   * @param {string} name - Zone name
   * @returns {SceneZone|null}
   */
  getSceneZone(name)
  {
    return this.sceneZones.find(zone => zone.name.toLowerCase() === name.toLowerCase()) ?? null
  }

  /**
   * Get a scene zone by index.
   * @param {number} index - Zone index
   * @returns {SceneZone|null}
   */
  getSceneZoneByIndex(index)
  {
    return this.sceneZones.find(zone => zone.index === index) ?? null
  }

  /**
   * Get all physics objects.
   * @returns {THREE.Object3D[]}
   */
  getPhysicsObjects()
  {
    return this.physicsObjects
  }

  /**
   * Get all interactable objects.
   * @returns {THREE.Object3D[]}
   */
  getInteractables()
  {
    return this.interactables
  }

  /**
   * Get site data.
   * @returns {SiteData}
   */
  getSiteData()
  {
    return this.siteData
  }

  /**
   * Get looping animations.
   * @returns {string[]}
   */
  getLoopingAnimations()
  {
    return this.animationManager.getLoopingAnimations()
  }

  /**
   * Populate scene zones from the loaded scene.
   * @private
   * @param {THREE.Object3D} scene - The scene to parse
   */
  populateSceneZones(scene)
  {
    scene.environmentIntensity = 1

    // Update world matrices ensures bounding box calculations are correct
    scene.updateMatrixWorld(true)

    let firstCamera = null

    // Use traverse to handle all nodes including nested groups
    // Collect nodes first to prevent issues if the scene graph is modified during processing
    const nodes = []
    scene.traverse(node =>
    {
      if (!nodes.includes(node)) nodes.push(node)
      node.children.forEach(child =>
      {
        if (!nodes.includes(child)) nodes.push(child)
      })
    })

    nodes.forEach(node =>
    {
      if (this.verbose)
      {
        debugLog('[SceneManager] GLB Node:', {
          name: node.name,
          node
        })
      }
      // Identify the first camera in the scene as a fallback
      if (node.isCamera && !firstCamera)
      {
        firstCamera = node
      }

      // Log all cameras found
      if (node.isCamera)
      {
        debugLog('[SceneManager] Camera node:', {
          name: node.name,
          cameraAnchorIndex: node.userData?.cameraAnchorIndex,
          type: node.userData?.type,
          position: node.position.toArray()
        })
      } else if (node.isMesh)
      {
        // calculate bounding box
        node.geometry.computeBoundingBox()
        node.geometry.computeBoundingSphere()
      }

      // Enable shadows based on userData or default to true
      node.castShadow = node.userData.castShadow !== false
      node.receiveShadow = node.userData.receiveShadow !== false

      // Parse animations and sounds
      this.animationManager.parseAnimations(node)
      this.audioManager.parseSounds(node)

      // Get zone assignment
      const userDataCopy = { ...node.userData }
      let sceneZone = null

      if (userDataCopy?.zone)
      {
        sceneZone = this.getOrCreateSceneZone(userDataCopy.zone)
      }

      // Parse the node
      this.parseNode(sceneZone, node)
    })

    // Fallback: If no zones/anchors defined, use the first GLB camera
    if (this.waypoints.length === 0 && firstCamera)
    {
      debugLog('[SceneManager] No waypoints found, using fallback camera:', firstCamera.name)
      // Assign a default index so it gets processed as a waypoint
      firstCamera.userData.cameraAnchorIndex = 1
      this.addCameraAnchor(null, firstCamera)
    }

    debugLog('[SceneManager] After populateSceneZones:')
    debugLog('[SceneManager] Total waypoints:', this.waypoints.length)
    debugLog('[SceneManager] Total scene zones:', this.sceneZones.length)
    this.waypoints.forEach((wp, i) =>
    {
      debugLog(`[SceneManager] Waypoint[${i}]:`, {
        index: wp.index,
        anchorName: wp.camera.anchor?.name,
        anchorPosition: wp.camera.anchor?.position?.toArray?.() || 'none'
      })
    })

    // Sort zones and waypoints by index
    this.sceneZones.sort((a, b) => a.index - b.index)
    this.waypoints.sort((a, b) => a.index - b.index)

    debugLog('[SceneManager] After sorting:')
    this.waypoints.forEach((wp, i) =>
    {
      debugLog(`[SceneManager] Waypoint[${i}] (sorted):`, {
        index: wp.index,
        anchorPosition: wp.camera.anchor?.position?.toArray?.() || 'none'
      })
    })
  }

  /**
   * Get or create a scene zone.
   * @private
   * @param {string} name - Zone name
   * @returns {SceneZone}
   */
  getOrCreateSceneZone(name)
  {
    let zone = this.sceneZones.find(z => z.name.toLowerCase() === name.toLowerCase())

    if (!zone)
    {
      zone = this.createSceneZone(name)
    }

    return zone
  }

  /**
   * Create a new scene zone.
   * @private
   * @param {string} name - Zone name
   * @returns {SceneZone}
   */
  createSceneZone(name)
  {
    const zone = {
      name,
      index: -1,
      camera: {
        anchor: null,
        target: new THREE.Box3(),
        targetPosition: new THREE.Vector3(),
      },
      objects: {
        count: 0,
        interactables: [],
      },
    }

    this.sceneZones.push(zone)
    return zone
  }

  /**
   * Parse a scene node and add to appropriate collections.
   * @private
   * @param {SceneZone|null} sceneZone - Target zone
   * @param {THREE.Object3D} object - Node to parse
   */
  parseNode(sceneZone, object)
  {
    const { worldPosition, worldRotation } = this.getWorldData(object)

    // Handle site properties
    if ('siteProperties' in object.userData)
    {
      this.parseSiteProperties(object)
      this.scene.remove(object)
      return
    }

    // Handle media objects - check for Media property or mediaType property
    if ('Media' in object.userData || 'mediaType' in object.userData)
    {
      debugLog('[SceneManager] Found media object:', {
        name: object.name,
        hasMediaProperty: 'Media' in object.userData,
        hasMediaType: 'mediaType' in object.userData,
        mediaType: object.userData.mediaType,
        mediaSrc: object.userData.mediaSrc,
        mediaTrigger: object.userData.mediaTrigger
      })
      this.parseMediaObject(object, worldPosition, worldRotation)

      // Log the type after parseMediaObject to confirm it's set
      debugLog('[SceneManager] After parseMediaObject, object.userData.type:', object.userData.type)
    }

    // Handle physics objects
    if ('Physics' in object.userData || 'physics_type' in object.userData)
    {
      object.userData.worldPosition = worldPosition
      object.userData.worldRotation = worldRotation
      this.physicsObjects.push(object)
    } else if (
      object.userData?.type === 'interactable' ||
      object.userData?.OnSelectAnimations ||
      object.userData?.OnPointerEnterAnimations ||
      object.userData?.OnPointerExitAnimations
    )
    {
      // Ensure type is set so SceneZone picks it up
      object.userData.type = 'interactable'
      debugLog('[SceneManager] Adding interactable from parseNode:', object.name)
      this.addInteractable(sceneZone, object)
    } else if (object.userData?.type === 'cameraAnchor')
    {
      // Only add camera anchors that are actual cameras with valid index (>= 1)
      const anchorIndex = object.userData.cameraAnchorIndex
      if (object.isCamera && typeof anchorIndex === 'number' && anchorIndex >= 1)
      {
        this.addCameraAnchor(sceneZone, object)
      } else
      {
        debugWarn('[SceneManager] Skipping invalid camera anchor:', {
          name: object.name,
          isCamera: object.isCamera,
          cameraAnchorIndex: anchorIndex,
          reason: !object.isCamera ? 'not a camera' : 'invalid index (must be >= 1)'
        })
      }
    }

    // Expand zone camera target
    if (sceneZone && object.userData?.type !== 'cameraAnchor')
    {
      const target = new THREE.Vector3()
      sceneZone.camera.target.expandByObject(object)
      sceneZone.camera.target.getCenter(target)
      sceneZone.camera.targetPosition = target
      sceneZone.objects.count++
    }
  }

  /**
   * Parse site properties from object userData.
   * @private
   * @param {THREE.Object3D} object - Object with site properties
   */
  parseSiteProperties(object)
  {
    const userData = object.userData

    // Initialize with all user data to ensure template properties are included
    this.siteData = { ...userData }

    // Ensure required site properties exist with defaults
    Object.assign(this.siteData, {
      siteTitle: userData.siteTitle || '',
      siteAuthor: userData.siteAuthor || '',
      siteDescription: userData.siteDescription || '',
      siteURL: userData.siteURL || '',
      siteIconURL: userData.siteIconURL || 'favicon.ico',
      splashScreenActive: userData.splashActive,
      splashScreenTitle: userData.splashTitle,
      splashScreenBody: userData.splashBody,
      splashScreenButton: userData.splashButton,
    })



    // Parse world configuration
    if (userData.worldConfig)
    {
      let bgColor = userData.background_color

      // Normalize background color to array
      if (bgColor && typeof bgColor === 'object' && '0' in bgColor && '1' in bgColor && '2' in bgColor)
      {
        bgColor = [ bgColor[ 0 ], bgColor[ 1 ], bgColor[ 2 ] ]
      }

      this.siteData.worldConfig = {
        environmentTexture: userData.environment_texture,
        background: userData.background,
        backgroundColor: bgColor,
        environmentIntensity: userData.environment_intensity,
      }
    }
  }

  /**
   * Parse a media object (video, audio, positional audio).
   * @private
   */
  parseMediaObject(object, worldPosition, worldRotation)
  {
    const userData = object.userData

    const mediaData = {
      object,
      worldPosition,
      worldRotation,
      mediaSrc: userData.mediaSrc,
      mediaTrigger: userData.mediaTrigger,
      volume: userData.mediaVolume ?? 1,
      audioPosition: userData.mediaAudioPosition ?? 'Global',
    }

    switch (userData.mediaType)
    {
      case 'Audio':
      case '3DPositionalAudio':
        // Treat all audio as global
        this.mediaObjects.audio.push(mediaData)
        this.markAudioAsInteractable(object, userData)
        break

      case 'Video':
        object.visible = false
        this.mediaObjects.video.push(mediaData)
        break

      default:
        debugLog('[SceneManager] Unknown mediaType:', userData.mediaType)
    }
  }

  /**
   * Mark an audio object as interactable if it has a click/hover trigger.
   * @private
   */
  markAudioAsInteractable(object, userData)
  {
    const isInteractive = userData.mediaTrigger === 'OnSelect' || userData.mediaTrigger === 'OnPointerEnter'
    if (isInteractive)
    {
      object.userData.type = object.userData.type || 'interactable'
      object.userData.interactableData = object.userData.interactableData || 'audio'
    }
  }

  /**
   * Get world position and rotation for an object.
   * @private
   * @param {THREE.Object3D} object - Target object
   * @returns {{ worldPosition: THREE.Vector3, worldRotation: THREE.Quaternion }}
   */
  getWorldData(object)
  {
    const worldPosition = new THREE.Vector3()
    object.getWorldPosition(worldPosition)

    const worldRotation = new THREE.Quaternion()
    object.getWorldQuaternion(worldRotation)


    const worldScale = new THREE.Vector3()
    object.getWorldScale(worldScale)

    return { worldPosition, worldRotation, worldScale }
  }

  /**
   * Add a camera anchor to a scene zone.
   * @private
   * @param {SceneZone|null} sceneZone - Target zone
   * @param {THREE.Object3D} anchorObject - Camera anchor object
   */
  addCameraAnchor(sceneZone, anchorObject)
  {
    const cameraAnchorIndex = anchorObject.userData.cameraAnchorIndex
    const keepContentInFrame = anchorObject.userData.keepContentInFrame || false

    anchorObject.visible = false

    debugLog('[SceneManager] addCameraAnchor:', {
      name: anchorObject.name,
      anchorObject,
      cameraAnchorIndex,
      keepContentInFrame,
      position: anchorObject.position.toArray(),
      quaternion: anchorObject.quaternion.toArray(),
      zoneName: sceneZone?.name || 'no zone'
    })

    if (sceneZone)
    {
      sceneZone.index = cameraAnchorIndex
      sceneZone.camera.anchor = anchorObject
      sceneZone.keepContentInFrame = keepContentInFrame
    }

    this.waypoints.push({
      index: cameraAnchorIndex,
      keepContentInFrame,
      camera: {
        anchor: anchorObject,
        target: new THREE.Box3(),
        targetPosition: new THREE.Vector3(),
      },
    })
  }

  /**
   * Add an interactable object to a scene zone.
   * @private
   * @param {SceneZone|null} sceneZone - Target zone
   * @param {THREE.Object3D} object - Interactable object
   */
  addInteractable(sceneZone, object)
  {
    this.interactables.push(object)
    const { worldPosition, worldRotation, worldScale } = this.getWorldData(object)

    // Create default zone if needed
    if (!sceneZone)
    {
      sceneZone = this.getOrCreateSceneZone(DEFAULT_INTERACTABLE_ZONE)
      sceneZone.camera.anchor = object
      object.userData.zone = DEFAULT_INTERACTABLE_ZONE
    }

    // Normalize animation arrays
    const ensureArray = (val) =>
    {
      if (Array.isArray(val)) return val
      if (typeof val === 'string')
      {
        return val.split(',').map(s => s.trim()).filter(Boolean)
      }
      return []
    }

    // Propagate interactable properties to children
    object.children.forEach(child =>
    {
      child.traverse(node =>
      {
        node.userData.name = node.name
        // CHILD STEALING FIX:
        // Do NOT propagate 'type' to children.
        // If we mark children as "interactable", SceneZone will interpret them as
        // separate entities and may remove them from their Group to hoist them up.
        // We want children to stay INSIDE the Group (which is the Interactable).
        // node.userData.type = object.userData.type
        node.userData.interactableType = object.userData.interactableType
        node.userData.interactableData = object.userData.interactableData
        node.userData.OnSelectAnimations = ensureArray(object.userData.OnSelectAnimations)
        node.userData.OnPointerEnterAnimations = ensureArray(object.userData.OnPointerEnterAnimations)
        node.userData.OnPointerExitAnimations = ensureArray(object.userData.OnPointerExitAnimations)
        node.userData.mediaTrigger = object.userData.mediaTrigger

        if (!object.userData.zone)
        {
          node.userData.zone = DEFAULT_INTERACTABLE_ZONE
        }
      })
    })

    // Normalize parent object arrays
    object.userData.OnSelectAnimations = ensureArray(object.userData.OnSelectAnimations)
    object.userData.OnPointerEnterAnimations = ensureArray(object.userData.OnPointerEnterAnimations)
    object.userData.OnPointerExitAnimations = ensureArray(object.userData.OnPointerExitAnimations)

    const rotation = new THREE.Euler().setFromQuaternion(worldRotation)

    sceneZone.objects.interactables.push({
      object,
      worldPosition,
      rotation,
      scale: worldScale
    })
  }

  /**
   * Fix empty scene zones by calculating camera targets from anchors.
   * @private
   */
  fixEmptyZones()
  {
    this.sceneZones.forEach(zone =>
    {
      if (zone.objects.count !== 0) return
      if (!zone.camera.target.isEmpty()) return

      const anchor = zone.camera.anchor
      if (!anchor) return

      // Calculate target from camera direction
      const direction = new THREE.Vector3(0, 0, -1)
      direction.applyQuaternion(anchor.quaternion)

      const targetPosition = new THREE.Vector3()
      targetPosition.copy(direction).add(anchor.position)

      zone.camera.targetPosition = targetPosition
      zone.camera.target.setFromCenterAndSize(targetPosition, new THREE.Vector3(1, 1, 1))
    })
  }

  /**
   * Fix waypoints by updating target positions and bounding boxes.
   * @private
   */
  fixWaypoints()
  {
    this.waypoints.forEach(waypoint =>
    {
      const sceneZone = this.getSceneZoneByIndex(waypoint.index)

      if (sceneZone)
      {
        // Copy both targetPosition AND target (Box3) from the scene zone
        waypoint.camera.targetPosition = sceneZone.camera.targetPosition
        waypoint.camera.target = sceneZone.camera.target

        // If the scene zone's target is empty, calculate from all visible meshes
        if (waypoint.camera.target.isEmpty())
        {
          debugLog('[SceneManager] fixWaypoints: SceneZone target is empty, calculating from visible meshes...')
          this.calculateFallbackBoundingBox(waypoint)

          // Propagate fallback target to scene zone
          if (sceneZone)
          {
            sceneZone.camera.target.copy(waypoint.camera.target)
            sceneZone.camera.targetPosition.copy(waypoint.camera.targetPosition)
          }
        }
        else
        {
          debugLog('[SceneManager] fixWaypoints: Linked waypoint to sceneZone:', {
            waypointIndex: waypoint.index,
            zoneName: sceneZone.name,
            targetPosition: waypoint.camera.targetPosition?.toArray?.() || 'none',
            targetBoxEmpty: waypoint.camera.target?.isEmpty?.() ?? true,
            targetBoxMin: waypoint.camera.target?.min?.toArray?.() || 'none',
            targetBoxMax: waypoint.camera.target?.max?.toArray?.() || 'none'
          })
        }
      } else
      {
        // Calculate from anchor direction
        const anchor = waypoint.camera.anchor
        if (!anchor) return

        const direction = new THREE.Vector3(0, 0, -1)
        direction.applyQuaternion(anchor.quaternion)

        const targetPosition = new THREE.Vector3()
        targetPosition.copy(direction).add(anchor.position)
        waypoint.camera.targetPosition = targetPosition

        // Calculate fallback bounding box
        this.calculateFallbackBoundingBox(waypoint)
      }
    })
  }

  /**
   * Calculate a fallback bounding box for a waypoint from visible scene meshes.
   * @private
   * @param {Object} waypoint - The waypoint to calculate bounding box for
   */
  calculateFallbackBoundingBox(waypoint)
  {
    const allBox = new THREE.Box3()
    let meshCount = 0

    // Calculate from all visible meshes in scene
    this.scene.traverse((object) =>
    {
      if (this.verbose)
      {
        debugLog('[SceneManager] calculateFallbackBoundingBox: Traversing object:', {
          name: object.name,
          type: object.type,
          userData: object.userData,
          visible: object.visible,
          isMesh: object.isMesh,
        })
      }
      if (!object.isMesh) return
      if (!object.visible) return
      if (object.userData?.type === 'cameraAnchor') return
      if (object.name?.startsWith('_')) return

      // Compute bounding box from geometry
      if (object.geometry)
      {
        // Compute fresh bounding box and sphere
        object.geometry.computeBoundingBox()
        object.geometry.computeBoundingSphere()

        const localBox = object.geometry.boundingBox.clone()
        localBox.applyMatrix4(object.matrixWorld)
        allBox.union(localBox)
        meshCount++
      }
    })

    if (!allBox.isEmpty())
    {
      waypoint.camera.target = allBox

      // Also update target position to center of box if not set
      if (!waypoint.camera.targetPosition || waypoint.camera.targetPosition.equals(new THREE.Vector3()))
      {
        const center = new THREE.Vector3()
        allBox.getCenter(center)
        waypoint.camera.targetPosition = center
      }

      // Mark as fallback so we don't draw debug bounds for these
      waypoint.camera.target.isFallback = true

      debugLog('[SceneManager] calculateFallbackBoundingBox: Computed from scene meshes:', {
        waypointIndex: waypoint.index,
        meshCount,
        targetBoxMin: allBox.min.toArray().map(v => v.toFixed(2)),
        targetBoxMax: allBox.max.toArray().map(v => v.toFixed(2))
      })
    }
    else
    {
      const defaultSize = new THREE.Vector3(2, 2, 2)
      const targetPos = waypoint.camera.targetPosition || new THREE.Vector3()
      waypoint.camera.target = new THREE.Box3()
      waypoint.camera.target.setFromCenterAndSize(targetPos, defaultSize)
      // Mark as fallback so we don't draw debug bounds for these
      waypoint.camera.target.isFallback = true

      debugLog('[SceneManager] calculateFallbackBoundingBox: No meshes found, using default box:', {
        waypointIndex: waypoint.index,
        targetPosition: targetPos.toArray(),
        targetBoxMin: waypoint.camera.target.min.toArray(),
        targetBoxMax: waypoint.camera.target.max.toArray()
      })
    }
  }

  /**
   * Fix scene zones by adjusting camera positions.
   * @private
   */
  fixZones()
  {
    this.fixEmptyZones()

    this.sceneZones.forEach(zone =>
    {
      if (zone.objects.count === 0 || !zone.camera.anchor) return

      const { position } = zone.camera.anchor
      const target = zone.camera.targetPosition
      const targetBox = zone.camera.target


      // Update camera properties
      this.controls.camera.fov = zone.camera.anchor.fov
      this.controls.camera.near = zone.camera.anchor.near
      this.controls.camera.far = zone.camera.anchor.far

      this.controls.camera.updateProjectionMatrix()

      // Orient camera to anchor's view direction first
      this.controls.update(0)

      let camDist = 0

      if (zone.keepContentInFrame)
      {
        // Fit to bounding box
        this.controls.fitToBox(targetBox, false)
        this.controls.update(0)

        camDist = this.controls.camera.position.distanceTo(target) * CAMERA_FRAMING_PADDING
      }

      // Move camera to anchor
      this.orbitCameraTo(position, target, camDist)
      this.controls.update(0)

      // Update anchor position
      zone.camera.anchor.position.copy(this.controls.camera.position)
      zone.camera.anchor.fov = this.controls.camera.fov
      zone.camera.anchor.near = this.controls.camera.near
      zone.camera.anchor.far = this.controls.camera.far

      // Update corresponding waypoint
      const waypoint = this.waypoints.find(w => w.index === zone.index)
      if (waypoint)
      {
        waypoint.camera.anchor.position.copy(this.controls.camera.position)
        waypoint.camera.targetPosition = zone.camera.targetPosition
        waypoint.camera.anchor.fov = zone.camera.anchor.fov
        waypoint.camera.anchor.near = zone.camera.anchor.near
        waypoint.camera.anchor.far = zone.camera.anchor.far
      }
    })

    // Set initial camera position to the first waypoint (which is at array index 0 after sorting)
    const firstWaypoint = this.waypoints[ 0 ]
    debugLog('[SceneManager] fixZones - Setting initial camera:')
    debugLog('[SceneManager] First waypoint:', firstWaypoint ? {
      index: firstWaypoint.index,
      anchorName: firstWaypoint.camera.anchor?.name,
      anchorPosition: firstWaypoint.camera.anchor?.position?.toArray?.() || 'none',
      targetPosition: firstWaypoint.camera.targetPosition?.toArray?.() || 'none'
    } : 'NO WAYPOINT FOUND')

    if (firstWaypoint)
    {
      debugLog('[SceneManager] Calling orbitCameraTo with:', {
        position: firstWaypoint.camera.anchor.position.toArray(),
        target: firstWaypoint.camera.targetPosition.toArray()
      })
      this.orbitCameraTo(
        firstWaypoint.camera.anchor.position,
        firstWaypoint.camera.targetPosition,
        1,
        false
      )
      debugLog('[SceneManager] Camera position after orbitCameraTo:', this.controls.camera.position.toArray())
    }
  }

  /**
   * Orbit camera to a position looking at a target.
   * @private
   * @param {THREE.Vector3} positionTarget - Desired camera position
   * @param {THREE.Vector3} lookTarget - Look-at target
   * @param {number} camDist - Camera distance
   * @param {boolean} damp - Enable damping
   */
  orbitCameraTo(positionTarget, lookTarget, camDist = 0, damp = false)
  {
    const camDistThreshold = positionTarget.distanceTo(lookTarget)

    // Ensure minimum distance
    if (camDist < camDistThreshold)
    {
      camDist = camDistThreshold
    }

    // Calculate final position
    const newPosition = positionTarget.clone()
      .sub(lookTarget)
      .setLength(camDist)
      .add(lookTarget)

    if (!this.controls) return

    this.controls.setLookAt(...newPosition.toArray(), ...lookTarget.toArray(), damp)
  }
}
